import React from 'react';
import {
    BrowserRouter,
    Routes,
    Route,
    Navigate,
} from 'react-router-dom';
import { isLoggedIn } from './auth';
import {OrgContextProvider} from './context/OrgContext';
import {UIContextProvider} from './context/UIContext';
import {ThemeContextProvider} from './context/ThemeContext';
import './styles/UIOverlays.css';
import Landing from './pages/Landing';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import Login from './pages/Login';
import Register from './pages/Register';
import Customers from './pages/Customers';
import Project from './pages/Project';
import PrivateRoute from './utils/PrivateRoute';
import AdminRoute from './utils/AdminRoute';
import SuperAdminRoute from './utils/SuperAdminRoute';
import Layout from './components/Layout';
import Settings from "./pages/Settings";
import EmployeeArea from "./pages/EmployeeArea";
import ProjectAcquisition from './pages/ProjectAcquisition';
import Products from './pages/Products';
import Leads from './pages/Leads';
import Contacts from "./pages/Contacts";
import KPI from "./pages/KPI";
import EmployeeProfiles from "./pages/EmployeeProfiles";
import AdminPanel from './pages/AdminPanel';
import DailyTimeTracking from './pages/DailyTimeTracking';
import EmployeeHours from './pages/EmployeeHours';
import EmployeeDetail from './pages/EmployeeDetail';
import Planning from './pages/Planning';
import ProjectTickets from './pages/ProjectTickets';
import MoveAllocations from './pages/MoveAllocations';
import AppActions from './pages/AppActions';
import Invoices from './pages/Invoices';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import ChangePassword from './pages/ChangePassword';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '16px', fontFamily: 'system-ui, sans-serif', color: '#333' }}>
                    <h1 style={{ fontSize: '20px', margin: 0 }}>Ein Fehler ist aufgetreten</h1>
                    <p style={{ margin: 0, color: '#666' }}>Bitte lade die Seite neu oder geh zurück.</p>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button onClick={() => window.location.reload()} style={{ padding: '8px 16px', cursor: 'pointer' }}>Neu laden</button>
                        <button onClick={() => { this.setState({ hasError: false }); window.history.back(); }} style={{ padding: '8px 16px', cursor: 'pointer' }}>Zurück</button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

export default function App() {
    return (
        <ErrorBoundary>
        <ThemeContextProvider>
        <UIContextProvider>
        <OrgContextProvider>
        <BrowserRouter>
            <Routes>
                <Route path="/" element={isLoggedIn() ? <Navigate to="/timetracking" replace /> : <Landing/>}/>
                <Route path="/login" element={<Login/>}/>
                <Route path="/register" element={<Register/>}/>
                <Route path="/impressum" element={<Impressum/>}/>
                <Route path="/datenschutz" element={<Datenschutz/>}/>
                <Route path="/forgot-password" element={<ForgotPassword/>}/>
                <Route path="/reset-password" element={<ResetPassword/>}/>
                <Route path="/verify-email" element={<VerifyEmail/>}/>
                <Route path="/change-password" element={
                    <PrivateRoute>
                        <ChangePassword/>
                    </PrivateRoute>
                }/>

                <Route path="/customers" element={
                    <PrivateRoute>
                        <Layout>
                            <Customers/>
                        </Layout>
                    </PrivateRoute>
                }/>

                <Route path="/kpi" element={
                    <PrivateRoute>
                        <Layout>
                            <KPI/>
                        </Layout>
                    </PrivateRoute>
                }/>

                <Route path="/employee_profile" element={
                    <PrivateRoute>
                        <Layout>
                            <EmployeeProfiles/>
                        </Layout>
                    </PrivateRoute>
                }/>

                <Route path="/projects" element={
                    <PrivateRoute>
                        <Layout>
                            <Project/>
                        </Layout>
                    </PrivateRoute>
                }/>

                <Route path="/project_acquisition" element={
                    <PrivateRoute>
                        <Layout>
                            <ProjectAcquisition/>
                        </Layout>
                    </PrivateRoute>
                }/>

                <Route path="/products" element={
                    <AdminRoute>
                        <Layout>
                            <Products/>
                        </Layout>
                    </AdminRoute>
                }/>

                <Route path="/leads" element={
                    <AdminRoute>
                        <Layout>
                            <Leads/>
                        </Layout>
                    </AdminRoute>
                }/>
                <Route path="/customer_contacts" element={
                    <PrivateRoute>
                        <Layout>
                            <Contacts/>
                        </Layout>
                    </PrivateRoute>
                }/>

                <Route path="/employee" element={
                    <PrivateRoute>
                        <Layout>
                            <EmployeeArea/>
                        </Layout>
                    </PrivateRoute>
                }/>

                <Route path="/settings" element={
                    <PrivateRoute>
                        <Layout>
                            <Settings/>
                        </Layout>
                    </PrivateRoute>
                }/>

                <Route path="/admin" element={
                    <SuperAdminRoute>
                        <Layout>
                            <AdminPanel/>
                        </Layout>
                    </SuperAdminRoute>
                }/>

                <Route path="/timetracking" element={
                    <PrivateRoute>
                        <Layout>
                            <DailyTimeTracking/>
                        </Layout>
                    </PrivateRoute>
                }/>

                {/* The same editor, acting on one employee's days. AdminRoute, not
                    PrivateRoute: the URL must not be reachable by editing it. */}
                <Route path="/timetracking/:userId" element={
                    <AdminRoute>
                        <Layout>
                            <DailyTimeTracking/>
                        </Layout>
                    </AdminRoute>
                }/>

                <Route path="/employee-hours" element={
                    <AdminRoute>
                        <Layout>
                            <EmployeeHours/>
                        </Layout>
                    </AdminRoute>
                }/>

                <Route path="/employee-hours/:userId" element={
                    <PrivateRoute>
                        <Layout>
                            <EmployeeDetail/>
                        </Layout>
                    </PrivateRoute>
                }/>

                <Route path="/project-tickets" element={
                    <PrivateRoute>
                        <Layout>
                            <ProjectTickets/>
                        </Layout>
                    </PrivateRoute>
                }/>

                {/* Admin-only: the workload view reads everyone's capacity and absences. */}
                <Route path="/planning" element={
                    <AdminRoute>
                        <Layout>
                            <Planning/>
                        </Layout>
                    </AdminRoute>
                }/>

                <Route path="/move-allocations" element={
                    <AdminRoute>
                        <Layout>
                            <MoveAllocations/>
                        </Layout>
                    </AdminRoute>
                }/>

                <Route path="/app-actions" element={<Navigate to="/settings?tab=app_actions" replace />}/>

                <Route path="/invoices" element={
                    <AdminRoute>
                        <Layout>
                            <Invoices/>
                        </Layout>
                    </AdminRoute>
                }/>


                <Route path="*" element={
                    <PrivateRoute>
                        <Layout>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh', gap: 12, fontFamily: 'system-ui, sans-serif' }}>
                                <h2 style={{ fontSize: 20, margin: 0 }}>Diese Seite existiert nicht</h2>
                                <p style={{ color: '#666', margin: 0 }}>Die URL wurde nicht gefunden.</p>
                                <a href="/timetracking" style={{ color: 'var(--color-primary)', fontWeight: 600 }}>Zur Startseite</a>
                            </div>
                        </Layout>
                    </PrivateRoute>
                }/>
            </Routes>
        </BrowserRouter>
        </OrgContextProvider>
        </UIContextProvider>
        </ThemeContextProvider>
        </ErrorBoundary>
    );
}
